/* eslint-disable import/no-cycle */
import {useRef, useState} from 'react';
import {difference, groupBy, set} from 'lodash';
import {useTranslation} from 'react-i18next';
import {Col, Row, Space, Typography} from 'antd';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPlus} from '@fortawesome/free-solid-svg-icons';
import SortableTable from '../tables/SortableTable';
import Table from '../tables/Table';
import {arrayMove} from '../../../utils/helpers';
import Transfer from '../transfers/Transfer';
import HelpTooltip from '../tooltips/HelpTooltip';
import Form from '../form/Form';
import Input from '../inputs/Input';
import Select from '../inputs/Select';
import styles from './WidgetSettingsForm.module.scss';
import Switch from '../inputs/Switch';
import ColorPicker from '../inputs/ColorPicker';
import HierarchyTree from '../inputs/hierarchyTree/HierarchyTree';
import DeleteButton from '../buttons/DeleteButton';
import Button from '../buttons/Button';
import {ValidationRules} from '../../../utils/validationRules';
import {sortAlphabetically} from '../tables/sorters';
import {useStore} from '../../../hooks/useStore';
import WidgetSettingFormField from './WidgetSettingFormField';
import {Operation} from '../../../models/operation';
import Tabs from '../tabs/Tabs';
import {iconSelectOptions} from '../../../utils/icon';

const {TabPane} = Tabs;

export const FieldWrapper = ({field, children, defaultValue, ...props}) => {
  const FieldLabel = () => (
    <span>
      {field.label}
      <HelpTooltip title={field.helpText}/>
    </span>
  );

  return (
    <Form.Item
      name={field.id}
      label={field.label ? <FieldLabel/> : ''}
      rules={field.validations}
      initialValue={field.defaultValue}
      onBlur={field.onBlur}
      {...props}
    >
      {children}
    </Form.Item>
  );
};

export const InputField = ({field, ...props}) => (
  <Input {...props}/>
);

export const SelectField = ({field, optionFilterProp = 'label', ...props}) => {
  const defaultKey = 'default';
  const getSelectOption = (group, option) => (
    <Select.Option
      key={`${group}_${option.value}`}
      value={group === defaultKey ? option.value : `${group}:${option.value}`}
      label={option.label}
    >
      <span>{option.label}</span>
      <span style={{float: 'right'}}>
        <HelpTooltip title={option.helpText}/>
      </span>
    </Select.Option>
  );

  const options = typeof field.options === 'function' ? field.options() : field.options;
  const grouped = groupBy(options, (option) => option.group?.key || 'default');
  const groups = Object.entries(grouped).filter(([key]) => key !== defaultKey);

  return (
    <span>
      <Select
        {...props}
        optionFilterProp={optionFilterProp}
        className={styles.selectField}
        allowClear={field.allowClear}
        showSearch={field.showSearch || undefined}
      >
        {groups?.map(([group, items]) => (
          <Select.OptGroup key={group} label={items[0].group.label}>
            {items?.map((option) => getSelectOption(group, option))}
          </Select.OptGroup>
        ))}
        {grouped[defaultKey]?.map((option) => getSelectOption(defaultKey, option))}
      </Select>
    </span>
  );
};

export const NumberField = ({field, ...props}) => (
  <Input
    addonAfter={field.unit ? field.unit : null}
    disabled={field.disabled ? field.disabled : false}
    {...props}
  />
);

export const BooleanField = ({field, ...props}) => (
  <Switch {...props}/>
);

export const PropertiesField = ({
  availableProperties = [],
  texts = {},
  value,
  onChange,
  hasStoredSettings,
  label,
  ...props
}) => {
  const {t} = useTranslation();
  const columns = [
    {
      title: texts.headerPropertyName || t('table.WidgetSettingsFormFields.headerPropertyName'),
      render: ({title, prefix}) => (prefix ? `${prefix} > ${title}` : title),
    },
  ];

  return (
    <>
      {label && (<div style={{paddingBottom: 4}}>{label}</div>)}
      <Transfer
        {...props}
        titles={[
          texts.hiddenProperties || t('table.WidgetSettingsFormFields.hiddenProperties'),
          texts.visibleProperties || t('table.WidgetSettingsFormFields.visibleProperties'),
        ]}
        locale={{
          itemUnit: texts.itemUnit || t('table.WidgetSettingsFormFields.itemUnit'),
          itemsUnit: texts.itemsUnit || t('table.WidgetSettingsFormFields.itemsUnit'),
          notFoundContent: texts.notFoundContent || t('table.WidgetSettingsFormFields.notFoundContent'),
          searchPlaceholder: texts.searchPlaceholder || t('table.WidgetSettingsFormFields.searchPlaceholder'),
          selectInvert: texts.selectInvert || t('table.WidgetSettingsFormFields.selectInvert'),
          selectAll: texts.selectAll || t('table.WidgetSettingsFormFields.selectAll'),
        }}
        showSelectAll
        showSearch
        targetKeys={value}
        onChange={onChange}
        filterOption={(inputValue, item) => item.title.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1}
        dataSource={availableProperties}
      >
        {({
          direction,
          filteredItems,
          onItemSelectAll,
          onItemSelect,
          selectedKeys: listSelectedKeys,
          disabled: listDisabled,
        }) => {
          const rowSelection = {
            getCheckboxProps: (item) => ({
              disabled: listDisabled || item.disabled,
              className: 'drag-visible',
            }),
            onSelectAll(selected, selectedRows) {
              const treeSelectedKeys = selectedRows
                .filter((item) => !item.disabled)
                .map((item) => item.key);
              const diffKeys = selected
                ? difference(treeSelectedKeys, listSelectedKeys)
                : difference(listSelectedKeys, treeSelectedKeys);
              onItemSelectAll(diffKeys, selected);
            },
            onSelect({key}, selected) {
              onItemSelect(key, selected);
            },
            selectedRowKeys: listSelectedKeys,
          };

          const onOrderUpdated = ({oldIndex, newIndex}) => onChange(arrayMove(filteredItems, oldIndex, newIndex)
            .map(({key}) => key));

          return (
            <>
              {direction === 'right'
                ? (
                  <SortableTable
                    showHeader={false}
                    showDragHandle
                    dataSource={filteredItems}
                    onOrderUpdated={onOrderUpdated}
                    columns={columns}
                    rowSelection={rowSelection}
                    onRow={({key, disabled: itemDisabled}) => ({
                      onClick: () => {
                        if (itemDisabled || listDisabled) {
                          return;
                        }
                        onItemSelect(key, !listSelectedKeys.includes(key));
                      },
                    })}
                    style={{pointerEvents: listDisabled ? 'none' : null, height: 300}}
                  />
                )
                : (
                  <Table
                    showHeader={false}
                    dataSource={filteredItems}
                    columns={columns}
                    rowSelection={rowSelection}
                    pagination={false}
                    onRow={({key, disabled: itemDisabled}) => ({
                      onClick: () => {
                        if (itemDisabled || listDisabled) return;
                        onItemSelect(key, !listSelectedKeys.includes(key));
                      },
                    })}
                    style={{pointerEvents: listDisabled ? 'none' : null, height: 300}}
                  />
                )}
            </>
          );
        }}
      </Transfer>
    </>
  );
};

export const ColorField = ({field, ...props}) =>
  <ColorPicker {...props}/>;

export const HierarchyTreeField = ({field, onChange, ...props}) => {
  const handleOnChange = (change) => {
    onChange(change);
    if (field.onChange) {
      field.onChange(change);
    }
  };

  return (
    <HierarchyTree
      allowClear
      isDisabled={field.isDisabled ? field.isDisabled : () => false}
      onChange={handleOnChange}
      {...props}
    />
  );
};

export const ModelPropertySelectField = ({field, form, ...props}) => {
  const {t} = useTranslation();
  const columns = (remove) => [
    {
      title: t('ModelPropertySelectField.label'),
      width: '30%',
      render: (listField) => (
        <Form.Item
          {...listField}
          name={[listField.name, 'label']}
          fieldKey={[listField.fieldKey, 'label']}
          wrapperCol={{sm: 24}}
          style={{marginBottom: 0}}
        >
          <Input placeholder={t('ModelPropertySelectField.label')}/>
        </Form.Item>
      ),
    },
    {
      title: t('ModelPropertySelectField.model'),
      width: '30%',
      render: ({...listField}) => (
        <Form.Item
          {...listField}
          name={[listField.name, 'model']}
          fieldKey={[listField.fieldKey, 'model']}
          rules={[ValidationRules.required]}
          wrapperCol={{sm: 24}}
          style={{marginBottom: 0}}
        >
          <Select
            onChange={(selection) => {
              const value = form.getFieldValue(field.id);
              if (value[listField.key].property) {
                value[listField.key].property = field.modelOptions
                  .find((model) => model.key === selection).properties[0].key;
              }
              form.setFieldsValue(value);
            }}
          >
            {field.modelOptions.sort((a, b) => sortAlphabetically(a.label, b.label)).map((model) => (
              <Select.Option value={model.key} key={model.key}>
                {model.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      ),
    },
    {
      title: t('ModelPropertySelectField.property'),
      width: '30%',
      render: (listField) => {
        const selectedModel = form.getFieldValue([field.id, listField.name, 'model']);
        const modelOptions = field.modelOptions.find((model) => model.key === selectedModel);
        const options = typeof modelOptions?.properties === 'function'
          ? modelOptions?.properties()
          : modelOptions?.properties;

        return (
          <Form.Item
            {...listField}
            name={[listField.name, 'property']}
            fieldKey={[listField.fieldKey, 'property']}
            rules={[ValidationRules.required]}
            wrapperCol={{sm: 24}}
            style={{marginBottom: 0}}
          >
            <Select disabled={!selectedModel}>
              {selectedModel && options.map((property) => (
                <Select.Option value={property.key} key={property.key}>
                  {property.title}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        );
      },
    },
    {
      title: '',
      render: (listField) => (
        <DeleteButton confirmType="none" onConfirm={() => remove(listField.name)}/>
      ),
    },
  ];

  return (
    <Form.List
      name={field.id}
      rules={field.validations}
      {...props}
    >
      {(listFields, {add, remove, move}) => (
        <Space direction="vertical" style={{width: '100%'}}>
          <SortableTable
            showHeader={false}
            showDragHandle
            dataSource={listFields}
            onOrderUpdated={
              ({oldIndex, newIndex}) => move(oldIndex, newIndex)
            }
            columns={columns(remove)}
          />
          <Button onClick={() => add()}>
            <FontAwesomeIcon icon={faPlus} style={{marginRight: '0.5em'}}/>
            {t('ModelPropertySelectField.new')}
          </Button>
        </Space>
      )}
    </Form.List>
  );
};

export const PropertiesSelectDisplayField = ({field, form, ...props}) => {
  let fieldValuePath = field.name || field.id;
  if (typeof fieldValuePath === 'string') {
    fieldValuePath = [fieldValuePath];
  }
  const {t} = useTranslation();
  const tableRef = useRef(null);
  const widthColContentByPercent = 45;
  const withColHeaderBySpan = Math.round(0.24 * widthColContentByPercent, 0); // ant: 1 row with 24 cols

  const columns = (remove) => [
    {
      title: t('propertiesSelectDisplayField.model'),
      width: `${widthColContentByPercent}%`,
      render: ({...fieldListElement}) => {
        const selectedProperty = form?.getFieldValue([...fieldValuePath, fieldListElement.name, 'property']);
        const optionSelected = field?.modelOptions?.find((x) => x.key === selectedProperty?.property);
        const selectedList = form.getFieldValue(field.id) || [];
        const usedProperties = selectedList.filter((o) => !!o).map((o) => o.property);
        const optionsList = field.modelOptions.filter((opt) => {
          if (opt.key === selectedProperty) {
            return true;
          }
          return !usedProperties.includes(opt.key);
        });
        return (
          <Form.Item
            {...fieldListElement}
            name={[fieldListElement.name, 'property']}
            fieldKey={[fieldListElement.fieldKey, 'property']}
            rules={[ValidationRules.required]}
            wrapperCol={{sm: 24}}
            style={{marginBottom: 0}}
          >
            <Select
              onChange={(selection) => {
                const fieldIdentifier = fieldValuePath[0];
                const [, ...path] = [...fieldValuePath, fieldListElement.name];
                const currentValue = form.getFieldValue(fieldIdentifier);
                const currentSelected = field.modelOptions.find((x) => x.key === selection);
                const updatedValue = set(currentValue, [...path, 'label'], currentSelected.title);
                form.setFieldsValue({
                  [fieldIdentifier]: updatedValue,
                });
              }}
            >
              {optionSelected && (
                <Select.Option value={optionSelected.key} key={optionSelected.key} label={optionSelected.title}>
                  {optionSelected.title}
                </Select.Option>
              )}
              {optionsList
                .sort((a, b) => sortAlphabetically(a.title, b.title))
                .map((model) => (
                  <Select.Option value={model.key} key={model.key} label={model.title}>
                    {model.title}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        );
      },
    },
    {
      title: t('propertiesSelectDisplayField.label'),
      width: `${widthColContentByPercent}%`,
      render: (listField) => (
        <Form.Item
          {...listField}
          name={[listField.name, 'label']}
          fieldKey={[listField.fieldKey, 'label']}
          rules={[ValidationRules.required]}
          wrapperCol={{sm: 24}}
          style={{marginBottom: 0}}
        >
          <Input placeholder={t('propertiesSelectDisplayField.label')}/>
        </Form.Item>
      ),
    },
    {
      title: '',
      render: (listField) => <DeleteButton confirmType="none" onConfirm={() => remove(listField.name)}/>,
    },
  ];
  const handleAdd = (addFunc) => {
    addFunc();
    const element = tableRef.current?.children[0]?.children[0]?.children[0];
    if (element) {
      setTimeout(() => {
        const height = element.children[0].offsetHeight;
        element.scrollTo(0, height);
      }, 0);
    }
  };

  return (
    <div className={styles.propertiesSelectDisplayField}>
      {field?.label ? <h4 className={styles.headerTitle}>{field.label}</h4> : ''}
      <div className={styles.contentWrapper}>
        <Row>
          <Col span={withColHeaderBySpan}>{t('propertiesSelectDisplayField.displayCol')}</Col>
          <Col className={styles.headerClass} span={withColHeaderBySpan}>
            {t('propertiesSelectDisplayField.displayName')}
          </Col>
        </Row>
        <Row className={styles.propertiesWrapper}>
          <Form.List name={field.id} rules={field.validations} {...props}>
            {(listFields, {add, remove, move}) => (
              <Space direction="vertical" style={{width: '100%'}}>
                <div ref={tableRef}>
                  <SortableTable
                    showHeader={false}
                    showDragHandle
                    dataSource={listFields}
                    onOrderUpdated={({oldIndex, newIndex}) => move(oldIndex, newIndex)}
                    columns={columns(remove)}
                    className={form.getFieldValue(fieldValuePath)?.length ? styles.autoWidth : styles.fullWidth}
                  />
                </div>
                <Button onClick={() => handleAdd(add)}>
                  <FontAwesomeIcon icon={faPlus} style={{marginRight: '0.5em'}}/>
                  {t('propertiesSelectDisplayField.new')}
                </Button>
              </Space>
            )}
          </Form.List>
        </Row>
      </div>
    </div>
  );
};

export const HierarchyTreeWorkplaceField = ({field, onChange, ...props}) => {
  const {t} = useTranslation();
  const store = useStore();

  const handleOnChange = (change) => {
    onChange(change);
    if (field.onChange) {
      field.onChange(change);
    }
  };

  return (
    <HierarchyTree
      allowClear
      isDisabled={(hierarchy) => !store.workplaceStore.workplaces.find((wp) => wp.hierarchyId === hierarchy.id)}
      onChange={handleOnChange}
      placeholder={t('workplace.model.one')}
      {...props}
    />
  );
};

export const CustomTabEditorField = ({field, form}) => {
  const {t} = useTranslation();
  const [refresh, setRefresh] = useState(0);
  const [activeTabKey, setActiveTabKey] = useState();
  const defaultTitle = t('activeOperationWidget.settings.modal.custom.defaultTabTitle');

  const tabFields = [
    {
      id: 'title',
      label: t('activeOperationWidget.settings.modal.custom.tabTitle.label'),
      helpText: t('activeOperationWidget.settings.modal.custom.tabTitle.helpText'),
      type: WidgetSettingFormField.fieldTypes.String,
      validations: [ValidationRules.required],
      defaultValue: defaultTitle,
      onBlur: () => setRefresh(refresh + 1),
    },
    {
      id: 'icon',
      label: t('activeOperationWidget.settings.modal.custom.tabIcon.label'),
      helpText: t('activeOperationWidget.settings.modal.custom.tabIcon.helpText'),
      type: WidgetSettingFormField.fieldTypes.Select,
      validations: [ValidationRules.required],
      defaultValue: 'infoCircle',
      options: iconSelectOptions,
    },
    {
      id: 'columnCount',
      label: t('activeOperationWidget.settings.modal.custom.tabColumnCount.label'),
      helpText: t('activeOperationWidget.settings.modal.custom.tabColumnCount.helpText'),
      type: WidgetSettingFormField.fieldTypes.Number,
      defaultValue: 1,
      validations: [ValidationRules.required, ValidationRules.minNumber(1), ValidationRules.maxNumber(12)],
    },
    {
      id: 'showEmpty',
      label: t('activeOperationWidget.settings.modal.custom.showEmpty.label'),
      helpText: t('activeOperationWidget.settings.modal.custom.showEmpty.helpText'),
      type: WidgetSettingFormField.fieldTypes.Boolean,
      defaultValue: false,
    },
  ];

  const onEdit = (targetKey, action, add, remove, currentTabCount) => {
    if (action === 'add') {
      add();
      setActiveTabKey(`tab-${currentTabCount}`);
    } else if (action === 'remove') {
      const index = parseInt(targetKey.substring(4), 10);
      const activeIndex = parseInt(activeTabKey.substring(4), 10);
      remove(index);
      if (activeIndex > index) {
        setActiveTabKey(`tab-${activeIndex - 1}`);
      } else if (index === currentTabCount - 1) {
        setActiveTabKey(`tab-${currentTabCount - 2}`);
      }
    }
  };

  return (
    <Form.List name={field.id}>
      {(fields, {add, remove}) => (
        <Tabs
          tabPosition={'left'}
          type={'editable-card'}
          onEdit={(targetKey, action) => onEdit(targetKey, action, add, remove, fields.length)}
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
        >
          {fields.map(({key, name, fieldKey, ...restField}) => (
            <TabPane
              tab={(
                <Typography.Text style={{width: 120, textAlign: 'left'}} ellipsis>
                  {form.getFieldValue([field.id, name, 'title']) || defaultTitle}
                </Typography.Text>
              )}
              key={`tab-${name}`}
              closable={fields.length > 1}
              forceRender
            >
              {tabFields.map((tf) => {
                const tabField = {
                  ...tf,
                  ...restField,
                  id: [name, tf.id],
                };
                return (
                  <div key={`${name}-${tf.id}`}>
                    <WidgetSettingFormField field={tabField} form={form}/>
                  </div>
                );
              })}
              <PropertyGroupEditorField parentName={name} field={field} form={form}/>
            </TabPane>
          ))}
        </Tabs>
      )}
    </Form.List>
  );
};

export const PropertyGroupEditorField = ({parentName, field, form}) => {
  const {t} = useTranslation();
  const store = useStore();
  const [refresh, setRefresh] = useState(0);
  const [activeTabKey, setActiveTabKey] = useState();
  const defaultTitle = t('activeOperationWidget.settings.modal.custom.defaultGroupTitle');

  const groupFields = [
    {
      id: 'title',
      label: t('activeOperationWidget.settings.modal.custom.groupTitle.label'),
      helpText: t(
        'activeOperationWidget.settings.modal.custom.groupTitle.helpText',
        {showGroupLabelOption: t('activeOperationWidget.settings.modal.custom.groupShowGroupLabel.label')}
      ),
      type: WidgetSettingFormField.fieldTypes.String,
      validations: [ValidationRules.required],
      defaultValue: defaultTitle,
      onBlur: () => setRefresh(refresh + 1),
    },
    {
      id: 'showGroupLabel',
      label: t('activeOperationWidget.settings.modal.custom.groupShowGroupLabel.label'),
      helpText: t('activeOperationWidget.settings.modal.custom.groupShowGroupLabel.helpText'),
      type: WidgetSettingFormField.fieldTypes.Boolean,
      defaultValue: true,
    },
    {
      id: 'columnCount',
      label: t('activeOperationWidget.settings.modal.custom.groupColumnCount.label'),
      helpText: t('activeOperationWidget.settings.modal.custom.groupColumnCount.helpText'),
      type: WidgetSettingFormField.fieldTypes.Number,
      defaultValue: 1,
      validations: [ValidationRules.required, ValidationRules.minNumber(1), ValidationRules.maxNumber(12)],
    },
    {
      id: 'showPropertyLabels',
      label: t('activeOperationWidget.settings.modal.custom.groupShowPropertyLabels.label'),
      helpText: t('activeOperationWidget.settings.modal.custom.groupShowPropertyLabels.helpText'),
      type: WidgetSettingFormField.fieldTypes.Boolean,
      defaultValue: true,
    },
    {
      id: 'properties',
      type: WidgetSettingFormField.fieldTypes.PropertiesSelectDisplayField,
      modelOptions: Operation.allDisplayableProperties(store),
      defaultValue: [],
    },
  ];

  const onEdit = (targetKey, action, add, remove, currentTabCount) => {
    if (action === 'add') {
      add();
      setActiveTabKey(`tab-${currentTabCount}`);
    } else if (action === 'remove') {
      const index = parseInt(targetKey.substring(4), 10);
      const activeIndex = parseInt(activeTabKey.substring(4), 10);
      remove(index);
      if (activeIndex > index) {
        setActiveTabKey(`tab-${activeIndex - 1}`);
      } else if (index === currentTabCount - 1) {
        setActiveTabKey(`tab-${currentTabCount - 2}`);
      }
    }
  };

  return (
    <Form.List name={[parentName, 'groups']}>
      {(fields, {add, remove}) => (
        <Tabs
          tabPosition={'top'}
          type={'editable-card'}
          onEdit={(targetKey, action) => onEdit(targetKey, action, add, remove, fields.length)}
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
        >
          {fields.map(({key, name, fieldKey, ...restField}) => (
            <TabPane
              tab={(
                <Typography.Text style={{maxWidth: 120, textAlign: 'left'}} ellipsis>
                  {form.getFieldValue([field.id, parentName, 'groups', name, 'title']) || defaultTitle}
                </Typography.Text>
              )}
              key={`tab-${name}`}
              closable={fields.length > 1}
              forceRender
            >
              {groupFields.map((tf) => {
                const groupField = {
                  ...tf,
                  ...restField,
                  id: [name, tf.id],
                  name: [field.id, parentName, 'groups', name, tf.id],
                };
                return (
                  <div key={groupField.id}>
                    <WidgetSettingFormField field={groupField} form={form}/>
                  </div>
                );
              })}
            </TabPane>
          ))}
        </Tabs>
      )}
    </Form.List>
  );
};
